import { z } from 'zod';
import { SourceSystem } from '../enums.js';
import { DecimalString, IsoDate, IsoDateTime, MoneyAmount, UuidString } from '../primitives.js';

/**
 * S17 — Project (lifecycle: planning → active → on_hold → completed → closed).
 * ERPNext blueprint + Odoo separation of concerns (status ≠ stage, MRP ↔ Project orthogonal).
 *
 * Финансовые baseline-поля denormalized для list-page perf (P12.3 activity persists).
 * Source of truth для computed metrics — engine `project-aggregator.ts` (P8) + `/metrics` endpoint.
 */

export const ProjectStatus = z.enum(['open', 'completed', 'cancelled']);
export type ProjectStatus = z.infer<typeof ProjectStatus>;

/**
 * project-visibility (SOLUTION §0) — доступность проекта. Отдельно от `status`
 * (где 'open' — жизненный статус записи). 'private' = только участники + admin.
 */
export const ProjectVisibility = z.enum(['open', 'private']);
export type ProjectVisibility = z.infer<typeof ProjectVisibility>;

export const ProjectStage = z.enum(['planning', 'active', 'on_hold', 'completed', 'closed']);
export type ProjectStage = z.infer<typeof ProjectStage>;

export const ProjectHealth = z.enum(['on_track', 'at_risk', 'overdue', 'on_hold']);
export type ProjectHealth = z.infer<typeof ProjectHealth>;

export const PercentCompleteMethod = z.enum([
  'manual',
  'task_completion',
  'task_progress',
  'task_weight',
]);
export type PercentCompleteMethod = z.infer<typeof PercentCompleteMethod>;

export const ProjectType = z.enum(['supply', 'manufacturing', 'rnd', 'modernization', 'other']);
export type ProjectType = z.infer<typeof ProjectType>;

export const PROJECT_TYPE_LABEL: Record<ProjectType, string> = {
  supply: 'Поставка',
  manufacturing: 'Производство',
  rnd: 'R&D',
  modernization: 'Модернизация',
  other: 'Прочее',
};

// ─── Shared label maps (для FE G4/G5/G10 — STATUS_LABEL pattern) ──────

export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  open: 'Открыт',
  completed: 'Завершён',
  cancelled: 'Отменён',
};

export const PROJECT_STAGE_LABEL: Record<ProjectStage, string> = {
  planning: 'Планирование',
  active: 'В работе',
  on_hold: 'На паузе',
  completed: 'Завершён',
  closed: 'Закрыт',
};

export const PROJECT_HEALTH_LABEL: Record<ProjectHealth, string> = {
  on_track: 'В графике',
  at_risk: 'Под угрозой',
  overdue: 'Просрочен',
  on_hold: 'На паузе',
};

export const PERCENT_COMPLETE_METHOD_LABEL: Record<PercentCompleteMethod, string> = {
  manual: 'Вручную',
  task_completion: 'Доля завершённых задач',
  task_progress: 'Средний прогресс задач',
  task_weight: 'Взвешенный прогресс',
};

// ─── Project entity ───────────────────────────────────────────────────

export const Project = z
  .object({
    id: UuidString,
    entity_id: UuidString,
    /** Server-generated `PROJ-YYYY-####`. */
    code: z.string().min(1),
    name: z.string().min(1).max(255),
    // task-quick-capture B1 — клиент опционален: проект-контейнер задач без клиента → null.
    customer_id: UuidString.nullable(),
    /** JOIN enrichment. */
    customer_name: z.string().nullable().optional(),
    sales_order_id: UuidString.nullable(),
    sales_order_code: z.string().nullable().optional(),
    project_type: ProjectType.nullable(),
    status: ProjectStatus.default('open'),
    /** open (вся компания) | private (только участники). SOLUTION §0. */
    visibility: ProjectVisibility.default('open'),
    stage: ProjectStage.default('planning'),
    health: ProjectHealth.default('on_track'),
    expected_start_date: IsoDate.nullable(),
    expected_end_date: IsoDate.nullable(),
    /** Computed via P8 from earliest task_log / WO start. */
    actual_start_date: IsoDateTime.nullable(),
    actual_end_date: IsoDateTime.nullable(),
    percent_complete: DecimalString.default('0'),
    percent_complete_method: PercentCompleteMethod.default('task_progress'),
    /** Financial baseline (user input). */
    estimated_cost: MoneyAmount,
    /** Computed denormalized (P12.3 persists после recompute). */
    total_cost_labor: MoneyAmount.optional(),
    total_cost_materials: MoneyAmount.optional(),
    total_cost_purchase: MoneyAmount.optional(),
    total_cost_landed: MoneyAmount.optional(),
    total_cost_actual: MoneyAmount.optional(),
    total_billed_amount: MoneyAmount.optional(),
    gross_margin: MoneyAmount.optional(),
    per_gross_margin: DecimalString.default('0'),
    // task-quick-capture B3 — производный флаг: проект финансовый/PM (клиент/заказ/бюджет)
    // vs лёгкий контейнер задач. Считает BE (mapProjectRow), FE рисует бейдж (F5).
    is_financial: z.boolean().optional(),
    /**
     * B9 (tier2-approval-requests §6) — активная заявка на действие над проектом.
     *
     * Производная от очереди, а не колонка: объект во время ожидания НЕ меняется,
     * рядом висит метка «ждёт согласования: отмена». `null` — заявок нет.
     */
    pending_approval: z
      .object({
        id: UuidString,
        task_type: z.string(),
        addressee_user_id: UuidString.nullable(),
        expires_at: IsoDateTime.nullable(),
      })
      .nullable()
      .optional(),
    pm_user_id: UuidString.nullable(),
    pm_user_name: z.string().nullable().optional(),
    notes: z.string().nullable(),
    workflow_id: z.string().nullable().optional(),
    source_system: SourceSystem.default('staiter'),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
  })
  .passthrough();
export type Project = z.infer<typeof Project>;

export const ProjectStatusUpdate = z
  .object({
    id: UuidString,
    project_id: UuidString,
    update_date: IsoDateTime,
    previous_health: ProjectHealth.nullable(),
    new_health: ProjectHealth,
    narrative: z.string().min(1),
    user_id: UuidString,
    user_name: z.string().nullable().optional(),
    created_at: IsoDateTime,
  })
  .passthrough();
export type ProjectStatusUpdate = z.infer<typeof ProjectStatusUpdate>;

// ─── Inputs ────────────────────────────────────────────────────────────

export const ProjectUpsertInput = z.object({
  name: z.string().min(3).max(255),
  // access-go-live 2026-07-08 — явный выбор компании при создании (мульти-доступ /
  // «Все компании»; без него проект молча падал в активную/JWT-дефолтную компанию).
  // '' → null (пустой Select). На PATCH поле игнорируется (whitelist updates в /:id/pm) —
  // перенос проекта между компаниями этим полем НЕ открывается.
  entity_id: z.preprocess((v) => (v === '' ? null : v), UuidString.nullable().optional()),
  // task-quick-capture B1 — клиент опционален: проект-контейнер задач не требует клиента
  // (customer_id в БД NULLABLE). Финансовая привязка — опциональный слой (в форме под аккордеоном).
  // preprocess '' → null: дефолт формы/пустой Select даёт '', который не uuid (иначе кнопка disabled).
  customer_id: z.preprocess((v) => (v === '' ? null : v), UuidString.nullable().optional()),
  sales_order_id: UuidString.nullable().optional(),
  project_type: ProjectType.nullable().optional(),
  expected_start_date: IsoDate.nullable().optional(),
  expected_end_date: IsoDate.nullable().optional(),
  // task-quick-capture B1 — пустая себестоимость (дефолт формы {amount:''}) → undefined,
  // иначе DecimalString не принимает '' → форма невалидна → кнопка disabled.
  estimated_cost: z.preprocess(
    (v) =>
      v && typeof v === 'object' && (v as { amount?: unknown }).amount === '' ? undefined : v,
    MoneyAmount.optional(),
  ),
  percent_complete_method: PercentCompleteMethod.optional(),
  pm_user_id: UuidString.nullable().optional(),
  notes: z.string().max(5000).nullable().optional(),
  // project-visibility (F1/F2) — свитчер «Открытый/Закрытый». На create применяется;
  // на PATCH visibility идёт через отдельный гейт (B9), но поле допустимо в input.
  visibility: ProjectVisibility.optional(),
  // project-visibility (B24, §3.8) — участники прямо на форме создания: pm-create
  // вставляет их ролью 'member' (создатель остаётся lead). На PATCH игнорируется
  // (member-CRUD — отдельные ручки /members с гейтом assertCanManageMembers).
  members: z.array(UuidString).optional(),
});
export type ProjectUpsertInput = z.infer<typeof ProjectUpsertInput>;

// access-go-live 2026-07-08 — перенос проекта между компаниями (см.
// docs/backlogs/BACKLOG-2026-07-08-project-move-entity.md). Отдельная операция,
// НЕ поле PATCH: каскад entity_id по поддереву + guard-ы (счета/WO-worklogs) +
// отвязка клиента/заказа только с явным detach_financial_refs.
export const ProjectMoveEntityInput = z.object({
  target_entity_id: UuidString,
  detach_financial_refs: z.boolean().optional(),
});
export type ProjectMoveEntityInput = z.infer<typeof ProjectMoveEntityInput>;

export const ProjectStageTransitionInput = z
  .object({
    target_stage: ProjectStage,
    reason: z.string().nullable().optional(),
    /**
     * B10 (tier2-approval-requests §5) — поля подписи УДАЛЕНЫ, а не помечены
     * устаревшими. Переход `completed`/`closed` — Tier 2, и он уходит заявкой
     * (`POST /approval-requests`). Оставить их «на совместимость» значило бы
     * сохранить путь, ради закрытия которого пак и написан.
     */
    idempotency_key: z.string().min(1).max(128).optional(),
    // zod-strict-ok: B10 — request-body; строгость отвергает тело с полями подписи.
  })
  // zod-strict-ok: request-body — строгость отвергает опечатку и снятые поля подписи (B10).
  .strict();
export type ProjectStageTransitionInput = z.infer<typeof ProjectStageTransitionInput>;

export const ProjectStatusUpdateInput = z.object({
  new_health: ProjectHealth,
  narrative: z.string().min(1).max(2000),
});
export type ProjectStatusUpdateInput = z.infer<typeof ProjectStatusUpdateInput>;

/** B2 — Cancel project (status transition, не stage). Tier 1 для planning, Tier 2 для active+on_hold. */
export const ProjectCancelInput = z
  .object({
    reason: z.string().min(10).max(2000),
    // B10 — поля подписи удалены: отмена запущенного проекта уходит заявкой (§5).
    idempotency_key: z.string().min(1).max(128).optional(),
    // zod-strict-ok: B10 — request-body; строгость и ЕСТЬ enforcement удаления полей
    // подписи: без неё тело с `approver_signature` молча обрезалось бы, и клиент,
    // не обновивший вызов, продолжал бы думать, что подписывает.
  })
  // zod-strict-ok: request-body — строгость отвергает опечатку и снятые поля подписи (B10).
  .strict();
export type ProjectCancelInput = z.infer<typeof ProjectCancelInput>;

export const ProjectFinancialBaselineInput = z.object({
  estimated_cost: MoneyAmount.optional(),
  percent_complete_method: PercentCompleteMethod.optional(),
  /**
   * B3 (project-task-progress §2.1) — ручное значение прогресса проекта, 0..100.
   *
   * Это ЕДИНСТВЕННЫЙ канонический write-путь для percent_complete [S-5]: здесь уже
   * есть reason ≥ 10 символов и audit-запись, а число, которое PM объявляет руками
   * вместо расчёта, обязано быть объяснено. `PATCH /pm` и прочие ручки значение не
   * принимают сознательно.
   *
   * Принимается только вместе с `percent_complete_method='manual'` (действующим или
   * назначаемым тем же запросом) — иначе 422: авто-метод всё равно перезатрёт значение
   * ближайшим пересчётом, и контрол оказался бы фиктивным (И-3).
   */
  percent_complete: DecimalString.refine((v) => Number(v) >= 0 && Number(v) <= 100, {
    message: 'percent_complete must be within 0..100',
  }).optional(),
});
export type ProjectFinancialBaselineInput = z.infer<typeof ProjectFinancialBaselineInput>;

/** P7.5 transition matrix. `cancelled` — это status, не stage (DB check constraint
 *  не allowлит). Отмена проекта — отдельный action, не stage transition. */
export const PROJECT_STAGE_MATRIX: Record<ProjectStage, readonly ProjectStage[]> = {
  planning: ['active'],
  active: ['on_hold', 'completed'],
  on_hold: ['active'],
  completed: ['closed', 'active'], // re-open if mistakenly closed
  closed: [],
} as const;
